import 'extensions/g-modal';
import GTM from 'extensions/gtm';
import 'extensions/ui-components/x-loader';
import {BreakpointEnum, breakpoints} from 'extensions/ui-mixins';
import {defineCustomElement} from 'extensions/utils';
import {LitElement, html, nothing} from 'lit';
import {createRef, ref} from 'lit/directives/ref.js';
import stylesheet from './x-mini-basket.component.scss?as=stylesheet';

const isMobile = () => {
	const mediaQueryList = window.matchMedia(breakpoints[BreakpointEnum.Mobile]);

	return mediaQueryList.matches;
};

export class MiniBasket extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			basketUrl: {
				attribute: 'basket-url',
				type: String
			},
			count: {
				type: Number
			},
			formattedSubtotal: {
				attribute: 'formatted-subtotal',
				type: String
			},
			loading: {
				type: Boolean
			}
		};
	}

	constructor () {
		super();

		this.modal = createRef();
		this.modalContent = createRef();
		this.gtm = new GTM(window.mivaJS);

		this.count = 0;
		this.basketUrl = '';
		this.formattedSubtotal = '';
		this.loading = false;
	}

	render () {
		return html`
			<g-modal
				${ref(this.modal)}
				lock="true"
				direction="right"
				animation="slide"
				header="false"
				content-padding="false"
				hide-close-button
				class="mini-basket-modal"
			>
				<div slot="content" class="mini-basket-inner" ${ref(this.modalContent)}>
					${this.renderHeader()}
					${this.renderContents()}
					${this.renderFooter()}
					${this.renderLoading()}
				</div>
			</g-modal>
		`;
	}

	renderHeader () {
		return html`
			<div class="mini-basket-header">
				<div class="mini-basket-header__heading">
					Your Bag
					${this.count ? html`<span class="mini-basket-header__count">(${this.count} Item${this.count > 1 ? 's' : nothing})</span>` : nothing}
				</div>
				<div class="mini-basket-header__close">
					<button @click="${() => this.modal?.value?.close()}" class="mini-basket-header__close-button" aria-label="Close Minibasket"></button>
				</div>
			</div>
		`;
	}

	renderContents () {
		if (!this.count) {
			return html`
				<div class="mini-basket-empty">
					<div class="mini-basket-empty__header">It's Empty in Here!</div>
					<div class="mini-basket-empty__text">Add items to get started.</div>
					<button @click="${this.handleStartShoppingClick}" class="primary-cta">Start Shopping</button>
				</div>
			`;
		}

		return html`
			<div class="mini-basket-contents">
				<slot></slot>
			</div>
		`;
	}

	renderFooter () {
		if (!this.count) {
			return nothing;
		}

		return html`
			<div class="mini-basket-footer">
				<div class="mini-basket-footer__subtotal">
					<div class="mini-basket-footer__subtotal-text">Subtotal:</div>
					<div class="mini-basket-footer__subtotal-value">${this.formattedSubtotal}</div>
				</div>
				<div class="mini-basket-footer__ctas">
					<div class="mini-basket-footer__cta">
						<a href="${this.basketUrl}" @click="${() => this.handleButtonClick('View Bag & Checkout')}" class="primary-cta">View Bag & Checkout</a>
					</div>
					<div class="mini-basket-footer__cta">
						<button @click="${this.handleContinueShoppingClick}" class="secondary-cta">Continue Shopping</button>
					</div>
				</div>

			</div>
		`;
	}

	renderLoading () {
		if (!this.loading) {
			return nothing;
		}

		return html`<x-loader class="loader" aspect-ratio="1/1"></x-loader>`;
	}

	handleStartShoppingClick () {
		this.handleButtonClick('Start Shopping');
		this.modal?.value?.close();
	}

	handleContinueShoppingClick () {
		this.handleButtonClick('Continue Shopping');
		this.modal?.value?.close();
	}

	handleButtonClick (label) {
		this.gtm.sendEvent({
			action: 'Click',
			category: 'Mini Basket',
			label: label,
			noninteractionHit: false
		});
	}

	/**
	 * Open the mini basket.
	 * @param {number} autoCloseMs The number of milliseconds to wait before automatically closing the mini basket. This functionality does not work on mobile.
	 * @param {number} autoCloseAfterHoverMs The number of milliseconds wait before automatically closing the mini basket after hoving over the mini basket. This functionality does not work on mobile.
	 */
	open (autoCloseMs = 0, autoCloseAfterHoverMs = autoCloseMs) {
		this.modal?.value?.open();

		if (autoCloseMs > 0 && !isMobile()) {
			// Hoist timer declaration for later use
			let timer = null;

			// Create event handlers
			const handleMouseover = () => {
				clearTimeout(timer);
				timer = null;
			};
			const handleMouseout = () => {
				if (!timer) {
					// eslint-disable-next-line no-use-before-define
					createTimer();
				}
			};

			const createTimer = (ms = autoCloseAfterHoverMs) => {
				timer = setTimeout(() => {
					this.close();
					this.modalContent?.value?.removeEventListener('mouseover', handleMouseover);
					this.modalContent?.value?.removeEventListener('mouseout', handleMouseout);
				}, ms);
			};

			// Define initial timeout
			createTimer(autoCloseMs);

			// Add listener stop the timer from completing if the user hovers over the modal content at any time
			this.modalContent?.value?.addEventListener('mouseover', handleMouseover);
			this.modalContent?.value?.addEventListener('mouseout', handleMouseout);
		}
	}

	/**
	 * Open the mini basket.
	 */
	close () {
		this.modal?.value?.close();
	}
};

defineCustomElement('x-mini-basket', MiniBasket);
